import React, { useEffect, useMemo, useState } from 'react';

import { Location } from 'history';
import Cookie from 'js-cookie';
import { useDispatch, useSelector } from 'react-redux';
import {
  matchPath,
  Redirect,
  Route,
  Switch,
  useRouteMatch,
} from 'react-router-dom';
import {
  saveUnbounceParamToLocalforage,
  UnbounceParam,
} from 'registration/helpers/experiment/xena';

import { clearErrorDialog } from 'shared/actions/errorActions';
import ErrorBoundary from 'shared/components/ErrorBoundary';
import ErrorDialog from 'shared/components/ErrorDialog';
import activateABTest, {
  getRegVariantChar,
} from 'shared/helpers/activateABTest';
import { parsePageQuery } from 'shared/helpers/query';
import { registerSignupIntent } from 'shared/helpers/tracking';
import withGATracker from 'shared/helpers/withGATracker';

import getGoogleClickID from '../helpers/getGoogleClickID';
import { getPreviewTrackingParams } from '../helpers/getPreviewTrackingParams';
import TechOnboardContainerDonkeykong from './TechOnboardContainerDonkeykong';

type LocationState = {
  isLiteOnboardingFlow?: boolean;
};

type TechOnboardContainerProps = {
  location: Location<LocationState>;
};

const TechOnboardContainer = ({ location }: TechOnboardContainerProps) => {
  const dispatch = useDispatch();
  const match = useRouteMatch();
  const techProfile = useSelector((state: any) => state.tech_profile);
  const uiState = useSelector((state: any) => state.uiState);

  const [regVariant] = useState<string | null>(() => {
    if (
      techProfile &&
      techProfile.onboard_variant &&
      (techProfile.signup_intent === 'hiring' ||
        location.state?.isLiteOnboardingFlow)
    ) {
      return activateABTest('techOnboarding', {
        variantOverride: techProfile.onboard_variant,
      });
    }
    return activateABTest('techOnboarding');
  });

  useEffect(() => {
    const initializeTracking = async () => {
      const query = parsePageQuery(location.search);

      const gclid = getGoogleClickID(query);
      if (gclid) {
        Cookie.set('gclid', gclid, { expires: 90 });
      }

      if (techProfile && techProfile.signup_intent) {
        registerSignupIntent(techProfile.signup_intent);
      }

      const mpOptions = getPreviewTrackingParams(
        query.preview_checked,
        query.preview_enrolled
      ) as any;

      const mpVariant = query.mp_variant;
      if (mpVariant) {
        mpOptions.variant = mpVariant;
      }
      mpOptions['Onboard Variant'] = regVariant;
      mixpanel.track('Sign Up Visited', mpOptions);

      await saveUnbounceParamToLocalforage(
        UnbounceParam.desired_location,
        query
      );
    };

    initializeTracking();
  }, [location.search, techProfile]);

  const closeErrorDialog = () => {
    dispatch(clearErrorDialog());
  };

  const funnelMatch = useMemo(
    () =>
      matchPath(location.pathname, {
        path: `${match.url}/:funnelChar`,
      }),
    [location.pathname, match.url]
  );
  const funnelChar = funnelMatch ? funnelMatch.params.funnelChar : '';
  const regVariantChar = getRegVariantChar(regVariant);

  if (regVariantChar !== funnelChar) {
    if (funnelChar === '_') {
      const disambiguationFunnelMatch = matchPath(location.pathname, {
        path: `${match.url}/_/:stepNumber`,
      });
      const stepNumber = disambiguationFunnelMatch
        ? disambiguationFunnelMatch.params.stepNumber
        : '';
      return (
        <Redirect
          to={{
            ...location,
            pathname: `${match.url}/${regVariantChar}/${stepNumber}`,
          }}
        />
      );
    }
    return (
      <Redirect
        to={{ ...location, pathname: `${match.url}/${regVariantChar}` }}
      />
    );
  }

  let errorDialog = null;
  const errorDialogMsg = uiState.get('errorDialogMessage');
  if (errorDialogMsg) {
    errorDialog = (
      <ErrorDialog message={errorDialogMsg} onClose={closeErrorDialog} />
    );
  }

  return (
    <ErrorBoundary>
      <div>
        <Switch>
          <Route
            component={TechOnboardContainerDonkeykong}
            path={`${match.url}/d`}
          />
          <Route
            component={TechOnboardContainerDonkeykong}
            path={`${match.url}/e`}
          />
        </Switch>
        {errorDialog}
      </div>
    </ErrorBoundary>
  );
};

export default withGATracker(TechOnboardContainer);
